import { useCallback, useEffect, useState } from "react";
import { UserDTO } from "../../types/User";
import { Sports } from "../../api/Sports";
import { Structures } from "../../api/Structure";
import { useToasts } from "../../contexts/toasts";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { TailSpin } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faExclamationTriangle,
  faInfoCircle,
  faRankingStar,
} from "@fortawesome/free-solid-svg-icons";
import { Flex, Pagination } from "antd";
import "./RankingsList.css";
import { PagedResults } from "../../models/generics";
import itIT from "antd/lib/locale/it_IT";
import { ConfigProvider } from "antd";

export default function RankingsList() {
  const { showToast } = useToasts();
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [totalRankings, setTotalRankings] = useState<number>();
  const [pageSize, setPageSize] = useState(40);
  const [currentPage, setCurrentPage] = useState(1);
  const [rankings, setRankings] = useState<UserDTO[]>();

  const initStructure = useCallback(() => {
    Structures.getHostStructure().then(
      ({ data }) => {
        Sports.getRankings({
          structureId: data.structure.id,
          page: currentPage,
          limit: pageSize,
        }).then(
          ({ data }) => {
            setRankings((data as PagedResults<UserDTO>).items);
            setTotalRankings((data as PagedResults<UserDTO>).totalItems);
          },
          () => {
            setError(true);
            showToast(
              "rankings-retrieve-error",
              "Errore nel reperire la classifica del Circolo, per favore riprova.",
              "",
              "danger"
            );
            setRankings([]);
          }
        );
      },
      () => {
        showToast(
          "structure-retrieve-error",
          "Errore nel reperire i dati della Struttura, per favore riprova.",
          "",
          "danger"
        );
        Cookies.remove(window.__env.AUTH_TOKEN_NAME || "get-move-token");
        Cookies.remove(window.__env.AUTH_USER_NAME || "get-move-user");
        navigate("/");
      }
    );
  }, [showToast, navigate, currentPage, pageSize]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = (_current: number, size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const reset = useCallback(() => {
    setRankings(undefined);
    setError(false);
    initStructure();
  }, [initStructure]);

  useEffect(() => {
    reset();
  }, [currentPage, pageSize, reset]);

  const renderToolbar = () => {
    return (
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0">Classifica Interna del Circolo</h4>
        </Col>
        <ConfigProvider locale={itIT}>
          <Pagination
            disabled={!rankings}
            total={totalRankings || 0}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} di ${total} Giocatori`
            }
            pageSize={pageSize}
            current={currentPage}
            onChange={handlePageChange}
            onShowSizeChange={handlePageSizeChange}
            showSizeChanger
            align="center"
          />
        </ConfigProvider>
      </Row>
    );
  };

  useEffect(() => {
    initStructure();
  }, [initStructure]);

  const renderRankings = useCallback(() => {
    const getUserImage = (item: UserDTO) => {
      return item.photo && item.photo !== "";
    };

    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );
    return rankings!.length === 0 ? (
      <Alert variant="info" className="py-2">
        <FontAwesomeIcon icon={faInfoCircle} />
        <span className="ms-2">Nessun Utente trovato</span>
      </Alert>
    ) : (
      <div className="d-block gap-3">
        {rankings!.map((ranking, index) => (
          <Flex
            key={index}
            align="center"
            justify="start"
            style={{ borderBottom: "1px solid lightgray" }}
            gap={12}
            className="w-100 p-3"
          >
            <small className="fw-bold me-2">{index + 1 + "°"}</small>
            <div
              className="rounded-circle justify-content-center text-center my-2"
              style={{
                minWidth: 60,
                minHeight: 60,
                backgroundImage: `url(${ranking.photo})`,
                backgroundColor: "lightgray",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                alignContent: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `2px solid #f25d0b`,
              }}
            >
              {!getUserImage(ranking) && (
                <span className="fw-bold">
                  {ranking.name.substring(0, 1) +
                    ranking.familyName.substring(0, 1)}
                </span>
              )}
            </div>
            <Row>
              <span className="fw-semibold">
                {ranking.name + " " + ranking.familyName}
              </span>
              <small className="fw-regular">
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                {`${ranking.email || ""}`}
              </small>
              <small className="fw-regular">
                <FontAwesomeIcon icon={faRankingStar} className="me-2" />
                {`Liv. ${ranking.level || "-"}`}
              </small>
              <small className="fw-regular">
                {`Partite Vinte: ${ranking.wonMatches || 0} - Partite Perse: ${
                  ranking.lostMatches || 0
                }`}
              </small>
            </Row>
          </Flex>
        ))}
      </div>
    );
  }, [rankings, error]);

  const renderAsyncContent = useCallback(() => {
    return rankings ? (
      renderRankings()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    );
  }, [rankings, renderRankings]);

  return (
    <Container fluid="md" className="mt-5">
      {renderToolbar()}
      {renderAsyncContent()}
    </Container>
  );
}
